import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserMetaCardComponent } from '../../shared/components/user-profile/user-meta-card/user-meta-card.component';
import { UserInfoCardComponent } from '../../shared/components/user-profile/user-info-card/user-info-card.component';
import { ComponentCardComponent } from '../../shared/components/common/component-card/component-card.component';
import { LabelComponent } from '../../shared/components/form/label/label.component';
import { SecurityService } from '../../services/security/security.service';
import { Usuario } from '../../domain/usuario.domain';
import { MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'app-profile',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UserMetaCardComponent,
    UserInfoCardComponent,
    ComponentCardComponent,
    LabelComponent,
    InputTextModule,
    ButtonModule,
  ],
  templateUrl: './profile.component.html',
  styles: ``
})
export class ProfileComponent implements OnInit {
  private securityService = inject(SecurityService);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);

  usuario: Usuario = {};
  formCambioPassword!: FormGroup;
  loading = signal<boolean>(false);

  // Signals para mostrar/ocultar contraseñas
  showOldPassword = signal<boolean>(false);
  showNewPassword = signal<boolean>(false);
  showNewRetypedPassword = signal<boolean>(false);

  ngOnInit(): void {
    this.initPasswordForm();

    // Primero obtener el usuario del observable
    this.securityService.usuario$.subscribe(user => {
      this.usuario = user;
      console.log('Usuario cargado desde observable:', this.usuario);
    });

    // Si no hay ID, intentar obtener del usuario logueado directamente
    if (!this.usuario.id && this.securityService.usuarioLogged?.id) {
      this.usuario = this.securityService.usuarioLogged;
      console.log('Usuario cargado desde usuarioLogged:', this.usuario);
    }

    // Si aún no hay ID, obtener desde el servidor
    if (!this.usuario.id) {
      this.securityService.getUserMe().subscribe({
        next: (user) => {
          this.usuario = user;
          console.log('Usuario cargado desde servidor:', this.usuario);
        },
        error: (err) => {
          console.error('Error al cargar usuario:', err);
        }
      });
    }
  }

  initPasswordForm(): void {
    // Formulario para cambio de contraseña (requiere contraseña actual)
    this.formCambioPassword = this.fb.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(7)]],
      newRetypedPassword: ['', [Validators.required, Validators.minLength(7)]]
    }, { validators: this.passwordsMatch });
  }

  passwordsMatch(group: FormGroup) {
    const newPassword = group.get('newPassword')?.value;
    const newRetypedPassword = group.get('newRetypedPassword')?.value;
    return newPassword === newRetypedPassword ? null : { passwordsMismatch: true };
  }

  togglePasswordVisibility(field: string): void {
    if (field === 'oldPassword') {
      this.showOldPassword.set(!this.showOldPassword());
    } else if (field === 'newPassword') {
      this.showNewPassword.set(!this.showNewPassword());
    } else if (field === 'newRetypedPassword') {
      this.showNewRetypedPassword.set(!this.showNewRetypedPassword());
    }
  }

  cambiarPassword(): void {
    if (this.formCambioPassword.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos correctamente',
        life: 3000
      });
      return;
    }

    const usuarioCambioPassword: Usuario = {
      id: this.usuario.id,
      oldPassword: this.formCambioPassword.value.oldPassword,
      newPassword: this.formCambioPassword.value.newPassword,
      newRetypedPassword: this.formCambioPassword.value.newRetypedPassword
    };

    this.loading.set(true);
    this.securityService.actualizarPasswordUsuario(usuarioCambioPassword).subscribe({
      next: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Contraseña Actualizada',
          detail: 'Tu contraseña se ha actualizado correctamente',
          life: 3000
        });
        this.formCambioPassword.reset();
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al cambiar contraseña:', error);

        let errorMsg = 'Error al cambiar la contraseña';
        if (error.error?.message) {
          errorMsg = error.error.message;
        } else if (error.status === 400) {
          errorMsg = 'La contraseña actual es incorrecta o las nuevas contraseñas no cumplen los requisitos';
        } else if (error.status === 401) {
          errorMsg = 'La contraseña actual es incorrecta';
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMsg,
          life: 5000
        });
      }
    });
  }
}
